import { StepGuard } from '@components/guards/StepGuard';
import { BALoader } from '@components/loader/BALoader';
import { SplashScreen } from '@components/loader/SplashScreen';
import { Suspense, useEffect, useState } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';

import { Notfound } from '@/pages/commons/notfound404';

import { RouterWithTokenHandling } from './RouterWithTokenHandling';
import ROUTES from './routes';

const router = createBrowserRouter([
  {
    path: ROUTES.HOME.PATH,
    errorElement: <Notfound />,
    element: <RouterWithTokenHandling />,
    HydrateFallback: BALoader,
    children: [
      {
        index: true,
        element: (
          <Navigate to={ROUTES.HOME.CHILDREN_ROUTES!.VALIDATION.PATH} replace />
        ),
      },

      {
        path: ROUTES.HOME.CHILDREN_ROUTES!.VALIDATION.PATH,
        lazy: async () => {
          const { ValidationView } = await import('../pages/steps/validation');
          return {
            Component: () => (
              <StepGuard requiredStep="validation">
                <ValidationView />
              </StepGuard>
            ),
          };
        },
      },
      {
        path: ROUTES.HOME.CHILDREN_ROUTES!.PERSONAL.PATH,
        lazy: async () => {
          const { PersonalInfo } = await import('../pages/steps/personal');
          return {
            Component: () => (
              <StepGuard requiredStep="personal">
                <PersonalInfo />
              </StepGuard>
            ),
          };
        },
      },
      {
        path: ROUTES.HOME.CHILDREN_ROUTES!.RESIDENCE.PATH,
        lazy: async () => {
          const { ResidenciaInfo } = await import('../pages/steps/residence');
          return {
            Component: () => (
              <StepGuard requiredStep="residence">
                <ResidenciaInfo />
              </StepGuard>
            ),
          };
        },
      },
      {
        path: ROUTES.HOME.CHILDREN_ROUTES!.FINANCE.PATH,
        lazy: async () => {
          const { FinanceView } = await import('../pages/steps/finance');
          return {
            Component: () => (
              <StepGuard requiredStep="finance">
                <FinanceView />
              </StepGuard>
            ),
          };
        },
      },
      {
        path: ROUTES.HOME.CHILDREN_ROUTES!.JOB.PATH,
        lazy: async () => {
          const { JobView } = await import('../pages/steps/job');
          return {
            Component: () => (
              <StepGuard requiredStep="job">
                <JobView />
              </StepGuard>
            ),
          };
        },
      },
      {
        path: ROUTES.HOME.CHILDREN_ROUTES!.REFERENCES.PATH,
        lazy: async () => {
          const { ReferencesView } = await import('../pages/steps/references');
          return {
            Component: () => (
              <StepGuard requiredStep="references">
                <ReferencesView />
              </StepGuard>
            ),
          };
        },
      },
      {
        path: ROUTES.HOME.CHILDREN_ROUTES!.CONFIRM.PATH,
        lazy: async () => {
          const { ConfirmationData } = await import('../pages/steps/confirm');
          return {
            Component: () => (
              <StepGuard requiredStep="confirm">
                <ConfirmationData />
              </StepGuard>
            ),
          };
        },
      },
      {
        path: ROUTES.HOME.CHILDREN_ROUTES!.FINISH.PATH,
        lazy: async () => {
          const { FinishStep } = await import('../pages/steps/finish');
          return {
            Component: () => <FinishStep />,
          };
        },
      },
    ],
  },
  { path: '*', element: <h1>404</h1> },
]);

export function AppRouter() {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Suspense fallback={<BALoader />}>
      <SplashScreen show={showSplash} />
      <RouterProvider router={router} />
    </Suspense>
  );
}
